/**
 * RSC Advanced — WhatsApp message builders (PURE; no React / no engine state).
 *
 * Mirrors the Simple-mode AI BESS 360 conventions (buildAtapStyleMsg):
 *   - bold via *…*
 *   - NO per-line prices on scope items (customer reads the final Cash + CC only)
 *   - RM formatting via toLocaleString
 *   - ☀️ / ⚡ / 💰 / 📈 emoji headers (message body only — never in UI buttons)
 *
 * The ROI Searcher proposes up to three plans (Fastest ROI / High-value / Max
 * savings). This builds ONE multi-plan message listing each plan's system spec,
 * monthly + yearly saving, ROI, and the Cash + CC 12/24/36/60 tiles.
 *
 * `lang` ∈ 'cn' | 'my' | 'en'. ccFromCash is passed in (engine pure helper) so we
 * never re-implement the CC conversion here.
 */

const CC_TERMS = [12, 24, 36, 60];
const rm = (v) => `RM ${Math.round(Number(v) || 0).toLocaleString()}`;

// One plan block. `plan` is a hydrated ROI-searcher entry:
//   { label:{cn,en,my}, kwp, panels, panelWatt, inverterLabel, battQty, battKwhEach,
//     monthlySaving, yearlySaving, roiYears, netCash } and ccFromCash for the CC tiles.
function planBlock(plan, lang, ccFromCash) {
    const isCN = lang === 'cn', isMY = lang === 'my';
    const lbl = isCN ? plan.label.cn : isMY ? plan.label.my : plan.label.en;
    const pw = Number(plan.panelWatt) || 640;
    const battDesc = plan.battQty > 0
        ? `${plan.battQty}x ${plan.battKwhEach}kWh ${isCN ? 'GoodWe 电池' : 'GoodWe Battery'}`
        : (isCN ? '无电池' : isMY ? 'Tiada Bateri' : 'No Battery');
    const inv = plan.inverterLabel ? ` · ${plan.inverterLabel}` : '';

    let m = `*${lbl}*\n`;
    m += `⚡ *${plan.kwp}kWp · ${plan.panels}x ${pw}W ${isCN ? '电板' : isMY ? 'Panel' : 'Panels'} · ${battDesc}${inv}*\n`;
    m += isCN
        ? `💰 省电: *${rm(plan.monthlySaving)}++/月* · *${rm(plan.yearlySaving)}/年*\n📈 回本 ROI: *${plan.roiYears} 年*\n`
        : isMY
        ? `💰 Jimat: *${rm(plan.monthlySaving)}++/bulan* · *${rm(plan.yearlySaving)}/tahun*\n📈 ROI: *${plan.roiYears} tahun*\n`
        : `💰 Savings: *${rm(plan.monthlySaving)}++/month* · *${rm(plan.yearlySaving)}/year*\n📈 ROI: *${plan.roiYears} yrs*\n`;
    const cashLabel = isCN ? '现金价' : isMY ? 'Harga Tunai' : 'Cash';
    m += `${cashLabel}: *${rm(plan.netCash)}*\n`;
    const instLabel = isCN ? '信用卡分期 0%' : isMY ? 'Ansuran Kad Kredit 0%' : 'Credit Card 0% Instalment';
    m += `${instLabel}:\n`;
    for (const term of CC_TERMS) {
        const v = ccFromCash ? ccFromCash(plan.netCash, term) : 0;
        m += `  · ${term} ${isCN ? '期' : isMY ? 'bulan' : 'months'}: *${rm(v)}*\n`;
    }
    return m;
}

/**
 * Build the full multi-plan WhatsApp message.
 *
 * @param {Object}   args
 * @param {Array}    args.plans       ordered hydrated plans (label/kwp/.../netCash)
 * @param {number}   args.bill        customer's monthly TNB bill (RM), for the header
 * @param {number}   args.usageKwh    monthly usage (kWh), for the header
 * @param {number}   args.dayPct      daytime usage % (0..100)
 * @param {string}   args.phaseMode   'single' | '3phase'
 * @param {string}   args.customerName
 * @param {string}   args.lang        'cn' | 'my' | 'en'
 * @param {Function} args.ccFromCash  engine pure helper (cash, term) → CC RM
 */
export function buildRoiSearcherMsg({ plans, bill, usageKwh, dayPct, phaseMode, customerName, lang, ccFromCash }) {
    const list = (plans || []).filter(Boolean);
    if (list.length === 0) return '';
    const isCN = lang === 'cn', isMY = lang === 'my';
    const pl = phaseMode === '3phase'
        ? (isCN ? '3 相' : isMY ? '3 Fasa' : '3 Phase')
        : (isCN ? '单相' : isMY ? 'Fasa Tunggal' : 'Single Phase');
    const cn = (customerName || '').trim().toUpperCase();
    const cname = cn ? cn + ' ' : '';

    let m = isCN ? `☀️ *${cname}${pl} 太阳能 AI BESS 360 · ROI 方案对比* ☀️\n\n`
          : isMY ? `☀️ *${cname}${pl} Solar AI BESS 360 · Perbandingan ROI* ☀️\n\n`
          : `☀️ *${cname}${pl} Solar AI BESS 360 · ROI Comparison* ☀️\n\n`;

    if (usageKwh > 0 || bill > 0) {
        const billStr = bill > 0 ? `*${rm(bill)}* · ` : '';
        m += isCN ? `电费: ${billStr}*${Math.round(usageKwh)}kWh*\n日间用电: *${dayPct}%*\n\n`
           : isMY ? `Bil: ${billStr}*${Math.round(usageKwh)}kWh*\nPenggunaan Siang: *${dayPct}%*\n\n`
           : `Bill: ${billStr}*${Math.round(usageKwh)}kWh*\nDaytime usage: *${dayPct}%*\n\n`;
    }

    m += list.map(p => planBlock(p, lang, ccFromCash)).join('\n');

    m += isCN ? `\n_价格含 SST，未含 SuRIA 政府回扣。最终方案以勘测为准。_`
       : isMY ? `\n_Harga termasuk SST, belum tolak rebat SuRIA. Cadangan akhir tertakluk pada tinjauan tapak._`
       : `\n_Prices include SST, before SuRIA government rebate. Final proposal subject to site survey._`;
    return m;
}

export default buildRoiSearcherMsg;
